import React from "react";
import AuthContext from "../../contexts/JWTContext";
import axios from "../../utility/customAxios";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from "../../components/ui/dialog";
import {Input} from "../../components/ui/input";
import {Checkbox} from "../../components/ui/checkbox";
import {Button} from "../../components/ui/button";
import {capitalize} from "@mui/material";
import {cn} from "../../components/ui/lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import {MoreVertical} from "lucide-react";
import {toast} from "../../components/ui/use-toast";
import {Role, listRoles, assignRolesToUser, getUserRoles} from "../../clients/metoro/rbac";
import {DataTable} from "../../components/ui/datatable/datatable";
import {ColumnDef} from "@tanstack/react-table";
import {MultiSelectorDropDown, DropDownItem} from "../../components/Input/MultiSelectorDropdown/MultiSelectorDropDown";

interface User {
    uuid: string;
    name: string;
    email: string;
    isAdmin: boolean;
    status: string;
    roles?: Role[];
}

export function Users() {
    const context = React.useContext(AuthContext);
    const [users, setUsers] = React.useState<User[]>([]);
    const [roles, setRoles] = React.useState<Role[]>([]);
    const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
    const [openAssignRolesModal, setOpenAssignRolesModal] = React.useState(false);
    const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);

    // Invite modal
    const [openInviteModal, setOpenInviteModal] = React.useState(false)
    const [inviteEmail, setInviteEmail] = React.useState("")
    const [isNewUserAdmin, setIsNewUserAdmin] = React.useState(false)
    const [inviteLink, setInviteLink] = React.useState("")
    const [showCopyTick, setShowCopyTick] = React.useState(false)
    const [inviteSelectedRoleItems, setInviteSelectedRoleItems] = React.useState<DropDownItem[]>([])
    const [availableRoleItems, setAvailableRoleItems] = React.useState<DropDownItem[]>([])

    // Reset Password modal
    const [openResetPasswordModal, setOpenResetPasswordModal] = React.useState(false)
    const [resetPasswordLink, setResetPasswordLink] = React.useState("")
    const [selectedUserEmail, setSelectedUserEmail] = React.useState("")
    const [currentUser, setCurrentUser] = React.useState<User | null>(null)

    // View Invite Link modal
    const [openViewLinkModal, setOpenViewLinkModal] = React.useState(false)
    const [viewInviteLink, setViewInviteLink] = React.useState("")

    const [triggerReload, setTriggerReload] = React.useState(false)
    // Delete modal
    const [userToDelete, setUserToDelete] = React.useState<User | null>(null)

    React.useEffect(() => {
        axios.get("/api/v1/account/organization").then((response) => {
            setUsers(response.data.organizationMembers)
        }).catch((e) => {
            console.error(e)
        })
    }, [triggerReload])

    React.useEffect(() => {
        if (!context?.user?.email) return;
        axios.get("/api/v1/account/organization").then((response) => {
            const users = response.data.organizationMembers;
            const foundUser = users.find((user: any) => user.email === context?.user?.email);
            if (foundUser) {
                setCurrentUser(foundUser);
            }
        }).catch((e) => {
            console.error(e)
        })
    }, [context?.user?.email]);

    // Load available roles when invite modal opens
    React.useEffect(() => {
        if (openInviteModal) {
            listRoles().then((roles) => {
                const roleItems = roles.map(role => ({
                    displayName: role.name,
                    value: role.uuid
                }));
                setAvailableRoleItems(roleItems);
            }).catch((error) => {
                console.error('Failed to load roles:', error);
                toast({
                    title: "Error",
                    description: "Failed to load available roles",
                    variant: "destructive"
                });
            });
        }
    }, [openInviteModal]);

    const loadUserRoles = async (user: User) => {
        try {
            const userRoles = await getUserRoles(user.uuid);
            setUsers(prevUsers => prevUsers.map(u =>
                u.uuid === user.uuid ? {...u, roles: userRoles} : u
            ));
            return userRoles;
        } catch (error) {
            console.error('Failed to load user roles:', error);
            toast({
                title: "Error",
                description: "Failed to load user roles",
                variant: "destructive"
            });
            return [];
        }
    };

    const handleOpenAssignRoles = async (user: User) => {
        setSelectedUser(user);
        setOpenAssignRolesModal(true);

        try {
            const [allRoles] = await Promise.all([
                listRoles()
            ]);
            setRoles(allRoles);

            const userRoles = user.roles || [];
            const currentRoleUUIDs = userRoles.map(r => r.uuid);
            setSelectedRoles(currentRoleUUIDs);

            setUsers(prevUsers => prevUsers.map(u =>
                u.uuid === user.uuid ? {...u, roles: userRoles} : u
            ));
        } catch (error) {
            console.error('Failed to load roles:', error);
            toast({
                title: "Error",
                description: "Failed to load roles",
                variant: "destructive"
            });
        }
    };

    const handleAssignRoles = async () => {
        if (!selectedUser) return;

        try {
            await assignRolesToUser(selectedUser.uuid, selectedRoles);
            await loadUserRoles(selectedUser);
            setOpenAssignRolesModal(false);
            toast({
                title: "Success",
                description: "Roles assigned successfully",
            });
        } catch (error) {
            console.error('Failed to assign roles:', error);
            toast({
                title: "Error",
                description: "Failed to assign roles",
                variant: "destructive"
            });
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setShowCopyTick(true)
                setTimeout(() => {
                    setShowCopyTick(false)
                }, 2000)
            })
            .catch((err) => console.error('Failed to copy:', err));
    };

    const handleViewInviteLink = async (email: string) => {
        try {
            const response = await axios.post(`/api/v1/account/organization/invite/link`, {
                email: email,
            });
            setViewInviteLink(response.data.inviteLink);
            setOpenViewLinkModal(true);
        } catch (error) {
            console.error('Failed to get invite link:', error);
        }
    };

    const handleResetPassword = async (email: string) => {
        try {
            const response = await axios.post(`/api/v1/account/reset-password-token`, {
                user_email: email
            });
            setResetPasswordLink(response.data.reset_link);
            setSelectedUserEmail(email);
            setOpenResetPasswordModal(true);
        } catch (error) {
            console.error('Failed to generate reset password link:', error);
        }
    };

    const handleDeleteUser = async (user: User) => {
        try {
            await axios.delete(`/api/v1/user?email=${encodeURIComponent(user.email)}&status=${encodeURIComponent(user.status)}`);
            setTriggerReload(prev => !prev);
            setUserToDelete(null);
        } catch (error) {
            console.error('Failed to delete user:', error);
        }
    };

    const columns: ColumnDef<User>[] = [
        {
            accessorKey: "name",
            header: "User",
            cell: ({row}) => row.original.name || "Unknown"
        },
        {
            accessorKey: "email",
            header: "Email"
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({row}) => {
                const status = capitalize(row.original.status);
                const indicatorColor = status === "Active" ? "bg-green-600" : "bg-blue-500";
                return (
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${indicatorColor}`}></div>
                        {status}
                    </div>
                );
            }
        },
        {
            accessorKey: "roles",
            header: "Roles",
            cell: ({row}) => row.original.roles && row.original.roles.length > 0
                ? row.original.roles.map(role => role.name).join(", ")
                : "None"
        },
        {
            id: "actions",
            cell: ({row}) => {
                const user = row.original;
                return <div className="flex justify-end">
                    <DropdownMenu>
                        <DropdownMenuTrigger>
                            <MoreVertical className="h-5 w-5"/>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="text-textmedium bg-backgrounddark rounded hover:cursor p-0">
                            <DropdownMenuItem
                                requiredPermission={{
                                    resource: "accessManagement/default/*",
                                    operation: "update"
                                }}
                                onClick={() => handleViewInviteLink(user.email)}
                                className="hover:text-textlight hover:bg-backgroundlight w-full">
                                View Invite Link
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                requiredPermission={{
                                    resource: "accessManagement/default/*",
                                    operation: "update"
                                }}
                                onClick={() => handleResetPassword(user.email)}
                                className="hover:text-textlight hover:bg-backgroundlight w-full">
                                Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                requiredPermission={{
                                    resource: "accessManagement/default/*",
                                    operation: "delete"
                                }}
                                onClick={() => {
                                    if (user.email !== currentUser?.email) {
                                        setUserToDelete(user)
                                    }
                                }}
                                disabled={user.email === currentUser?.email}
                                className={cn(
                                    "hover:text-textlight hover:bg-backgroundlight w-full",
                                    user.email === currentUser?.email && "opacity-50 cursor-not-allowed hover:bg-transparent hover:text-textmedium"
                                )}>
                                Remove User
                            </DropdownMenuItem>
                            {user.status.toLowerCase() == "active" && <DropdownMenuItem
                                requiredPermission={{
                                    resource: "accessManagement/default/*",
                                    operation: "update"
                                }}
                                onClick={() => handleOpenAssignRoles(user)}
                                className="hover:text-textlight hover:bg-backgroundlight w-full">
                                Manage Roles
                            </DropdownMenuItem>}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            }
        }
    ];

    // Add new dialog for role assignment
    const RoleAssignmentDialog = () => (
        <Dialog open={openAssignRolesModal} onOpenChange={setOpenAssignRolesModal}>
            <DialogContent className="w-1/2 text-textmedium h-[800px]">
                <DialogHeader>
                    <DialogTitle>Assign Roles</DialogTitle>
                    <DialogDescription className={"text-textdark"}>
                        Select roles to assign to {selectedUser?.email}
                    </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                    {roles.length > 0 ? (
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h3 className="font-medium">Currently Assigned Roles</h3>
                                <div
                                    className="space-y-3 pl-2 border rounded p-4 h-[500px] bg-backgroundlight overflow-y-auto">
                                    {roles.filter(role => selectedRoles.includes(role.uuid)).map((role) => (
                                        <div key={role.uuid} className="flex items-start space-x-2">
                                            <Checkbox
                                                id={role.uuid}
                                                checked={true}
                                                onCheckedChange={(checked) => {
                                                    if (!checked) {
                                                        setSelectedRoles(selectedRoles.filter(id => id !== role.uuid));
                                                    }
                                                }}
                                            />
                                            <div className="grid gap-1.5 leading-none">
                                                <label
                                                    htmlFor={role.uuid}
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                >
                                                    {role.name}
                                                </label>
                                                {role.description && (
                                                    <p className="text-xs text-textdark truncate">
                                                        {role.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {selectedRoles.length === 0 && (
                                        <div className="text-sm text-textdark">No roles currently assigned</div>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <h3 className="font-medium">Available Roles</h3>
                                <div
                                    className="space-y-3 pl-2 border rounded p-4 h-[500px] bg-backgroundlight overflow-y-auto">
                                    {roles.filter(role => !selectedRoles.includes(role.uuid)).map((role) => (
                                        <div key={role.uuid} className="flex items-start space-x-2">
                                            <Checkbox
                                                id={role.uuid}
                                                checked={false}
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        setSelectedRoles([...selectedRoles, role.uuid]);
                                                    }
                                                }}
                                            />
                                            <div className="grid gap-1.5 leading-none">
                                                <label
                                                    htmlFor={role.uuid}
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                >
                                                    {role.name}
                                                </label>
                                                {role.description && (
                                                    <p className="text-xs text-textdark truncate">
                                                        {role.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {roles.filter(role => !selectedRoles.includes(role.uuid)).length === 0 && (
                                        <div className="text-sm text-textdark">No additional roles available</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-textdark">
                            No roles available to assign
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        onClick={handleAssignRoles}
                        className="text-textmedium border rounded bg-primarytransparent border-primary"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    return (
        <div>
            <div className={"flex justify-between mb-4"}>
                <h2 className={"text-lg font-semibold"}>Users</h2>
                <div className={"flex flex-col justify-center"}>
                    {/* Existing Invite Modal */}
                    <Dialog open={openInviteModal}>
                        <DialogContent className={"w-1/2"} onInteractOutside={() => {
                            setOpenInviteModal(false)
                            setInviteLink("")
                            setShowCopyTick(false)
                            setInviteSelectedRoleItems([])
                        }}>
                            <div className={"flex flex-col gap-4 text-textmedium"}>
                                {!inviteLink ? (
                                    <>
                                        <h3 className={"text-lg font-bold"}>Invite a user</h3>
                                        <div className={"text-textmedium"}>Enter the email of the user you want to
                                            invite.
                                        </div>
                                        <Input
                                            value={inviteEmail}
                                            onChange={(e) => setInviteEmail(e.target.value)}
                                            type={"email"} placeholder={"Email"}
                                            className={"border border-primary rounded p-2"}/>
                                        <div className="flex flex-col gap-2">
                                            <div className={"text-textmedium"}>Add the roles you want to assign to
                                                the user.
                                            </div>
                                            <MultiSelectorDropDown
                                                overrideEmtpySelectionText={"Select roles"}
                                                selectorSingular="Role"
                                                possibleItems={availableRoleItems}
                                                selectedItems={inviteSelectedRoleItems}
                                                setSelectedItems={setInviteSelectedRoleItems}
                                                visualSettings={{
                                                    dropdownAlign: "start"
                                                }}
                                            />
                                        </div>
                                        <div className={"flex justify-end gap-4"}>
                                            <Button
                                                onClick={() => {
                                                    setOpenInviteModal(false)
                                                    setInviteEmail("")
                                                    setIsNewUserAdmin(false)
                                                    setInviteSelectedRoleItems([])
                                                }}
                                                className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>Cancel</Button>
                                            <Button
                                                onClick={() => {
                                                    axios.post("/api/v1/account/organization/invite", {
                                                        newUserEmail: inviteEmail,
                                                        isAdmin: isNewUserAdmin,
                                                        roleUUIDs: inviteSelectedRoleItems.map(item => item.value)
                                                    }).then(response => {
                                                        setInviteLink(response.data.inviteLink)
                                                        setTimeout(() => {
                                                            setTriggerReload(prev => !prev)
                                                        }, 1000)
                                                    }).catch(e => console.error(e))
                                                }}
                                                disabled={inviteSelectedRoleItems.length === 0}
                                                className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"}>Invite</Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <h3 className={"text-lg font-bold"}>Invitation sent!</h3>
                                        <div className={"text-textmedium"}> We've emailed the invite to
                                            the user. You can also share this sign-up link with them:
                                        </div>
                                        <div className={"flex gap-2"}>
                                            <Input
                                                value={inviteLink}
                                                readOnly
                                                className={"border border-primary rounded p-2 flex-grow"}
                                            />
                                            <Button
                                                onClick={() => copyToClipboard(inviteLink)}
                                                className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded min-w-[80px]"}>
                                                {showCopyTick ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                                                         viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd"
                                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                              clipRule="evenodd"/>
                                                    </svg>
                                                ) : (
                                                    "Copy"
                                                )}
                                            </Button>
                                        </div>
                                        <div className={"flex justify-end"}>
                                            <Button
                                                onClick={() => {
                                                    setOpenInviteModal(false)
                                                    setInviteLink("")
                                                    setInviteEmail("")
                                                    setIsNewUserAdmin(false)
                                                    setShowCopyTick(false)
                                                    setInviteSelectedRoleItems([])
                                                }}
                                                className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded"}>
                                                Close
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* View Invite Link Modal */}
                    <Dialog open={openViewLinkModal}>
                        <DialogContent className={"w-1/3"} onInteractOutside={() => {
                            setOpenViewLinkModal(false)
                            setViewInviteLink("")
                            setShowCopyTick(false)
                        }}>
                            <div className={"flex flex-col gap-4 text-textmedium"}>
                                <h3 className={"text-lg font-bold"}>Invite Link</h3>
                                <div className={"text-textmedium"}>Share this sign-up link with the user:</div>
                                <div className={"flex gap-2"}>
                                    <Input
                                        value={viewInviteLink}
                                        readOnly
                                        className={"border border-primary rounded p-2 flex-grow"}
                                    />
                                    <Button
                                        onClick={() => copyToClipboard(viewInviteLink)}
                                        className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded min-w-[80px]"}>
                                        {showCopyTick ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                                                 viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd"
                                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                      clipRule="evenodd"/>
                                            </svg>
                                        ) : (
                                            "Copy"
                                        )}
                                    </Button>
                                </div>
                                <div className={"flex justify-end"}>
                                    <Button
                                        onClick={() => {
                                            setOpenViewLinkModal(false)
                                            setViewInviteLink("")
                                            setShowCopyTick(false)
                                        }}
                                        className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded"}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Reset Password Modal */}
                    <Dialog open={openResetPasswordModal}>
                        <DialogContent className={"w-1/3"} onInteractOutside={() => {
                            setOpenResetPasswordModal(false)
                            setResetPasswordLink("")
                            setShowCopyTick(false)
                        }}>
                            <div className={"flex flex-col gap-4 text-textmedium"}>
                                <h3 className={"text-lg font-bold"}>Reset Password Link</h3>
                                <div className={"text-textmedium"}>Share this reset password link
                                    with {selectedUserEmail}:
                                </div>
                                <div className={"flex gap-2"}>
                                    <Input
                                        value={resetPasswordLink}
                                        readOnly
                                        className={"border border-primary rounded p-2 flex-grow"}
                                    />
                                    <Button
                                        onClick={() => copyToClipboard(resetPasswordLink)}
                                        className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded min-w-[80px]"}>
                                        {showCopyTick ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                                                 viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd"
                                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                      clipRule="evenodd"/>
                                            </svg>
                                        ) : (
                                            "Copy"
                                        )}
                                    </Button>
                                </div>
                                <div className={"flex justify-end"}>
                                    <Button
                                        onClick={() => {
                                            setOpenResetPasswordModal(false)
                                            setResetPasswordLink("")
                                            setShowCopyTick(false)
                                        }}
                                        className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded"}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Delete User Confirmation Dialog */}
                    <Dialog open={!!userToDelete}>
                        <DialogContent className={"w-1/3"}>
                            <div className={"flex flex-col gap-4 text-textmedium"}>
                                <h3 className={"text-lg font-bold"}>Delete User</h3>
                                <div>Are you sure you want to delete this user?</div>
                                <div
                                    className={"text-base font-semibold text-textdark bg-backgroundlight px-2 py-1 rounded w-full"}>
                                    {userToDelete?.email}
                                </div>
                                <div className={"flex justify-end gap-2"}>
                                    <Button className={"text-textmedium bg-red-500/10 border border-red-500"}
                                            onClick={() => setUserToDelete(null)}>
                                        Cancel
                                    </Button>
                                    <Button className={"text-textmedium bg-primarytransparent border border-primary"}
                                            onClick={() => handleDeleteUser(userToDelete!)}>
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                    <Button
                        requiredPermission={{
                            resource: "accessManagement/default/*",
                            operation: "create"
                        }}
                        onClick={() => setOpenInviteModal(true)}
                        className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>Invite</Button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={users}
                textFilteringColumnName="email"
                textFilteringPlaceholder="Filter by email..."
                filters={[]}
                showPagination={true}
            />

            {RoleAssignmentDialog()}
        </div>
    );
}